/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { ToggleMenu } from '@opengeoweb/shared';
import { Copy, Delete } from '@opengeoweb/theme';
import { useCoreTranslation } from '../../../../../utils/i18n';
import SelectableFeaturesCheckboxConnect from '../SelectableFeaturesCheckbox/SelectableFeaturesCheckboxConnect';

interface LayerManagerButtonProps {
  mapId: string;
  layerId?: string;
  isFeatureLayer?: boolean;
  onLayerDuplicate?: (payload: { mapId: string; layerId: string }) => void;
  onLayerCopyToOtherMaps?: () => void;
  onLayerRemoveFromOtherMaps?: () => void;
  tooltipTitle?: string;
  icon?: React.ReactNode;
}

const LayerManagerMenuButton: React.FC<LayerManagerButtonProps> = ({
  mapId,
  layerId,
  isFeatureLayer = false,
  onLayerDuplicate,
  onLayerCopyToOtherMaps,
  onLayerRemoveFromOtherMaps,
  tooltipTitle,
  icon,
}: LayerManagerButtonProps) => {
  const { t } = useCoreTranslation();
  const nonEmptyTooltipTitle = tooltipTitle ?? t('layermanager-options');

  const onClickDuplicate = (): void => {
    onLayerDuplicate!({ mapId, layerId: layerId! });
  };

  const onClickCopyToOtherMaps = (): void => {
    onLayerCopyToOtherMaps?.();
  };

  const onClickRemoveFromOtherMaps = (): void => {
    onLayerRemoveFromOtherMaps?.();
  };

  return (
    <ToggleMenu
      buttonIcon={icon}
      buttonSx={{
        margin: '4px 2px',
        '&.MuiButtonBase-root': {
          backgroundColor: 'transparent',
          color: 'geowebColors.greys.accessible',
        },
      }}
      buttonTestId="openMenuButton"
      menuItems={[
        {
          text: t('layermanager-layer-duplicate'),
          action: onClickDuplicate,
          icon: <Copy />,
        },
        {
          text: t('layermanager-layer-duplicate-to-other-maps'),
          action: onClickCopyToOtherMaps,
          icon: <Copy />,
        },
        {
          text: t('layermanager-layer-remove-from-other-maps'),
          action: onClickRemoveFromOtherMaps,
          icon: <Delete />,
        },
      ]}
      menuPosition="bottom"
      tooltipTitle={nonEmptyTooltipTitle}
    >
      {!!isFeatureLayer && (
        <SelectableFeaturesCheckboxConnect layerId={layerId!} />
      )}
    </ToggleMenu>
  );
};

export default LayerManagerMenuButton;
